// Section assets can execute again after a Theme Editor reload.
var NewHeaderNavigation = customElements.get('new-header-navigation') || class extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.closeDelay = 160;
    this.activeTrigger = null;
    this.activePanel = null;
    this.abortController = new AbortController();
    this.desktopMedia = window.matchMedia('(min-width: 1200px)');
    this.triggers = [...this.querySelectorAll('[data-new-header-trigger]')];
    this.links = [...this.querySelectorAll('[data-new-header-link]')];
    this.panels = [...this.querySelectorAll('[data-new-header-panel]')];
    this.overlay = this.querySelector('[data-new-header-overlay]');
    this.menuControls = [...this.querySelectorAll('.header__menu > :is(a, [role="button"]), .header__menu > expose-element > a')];
    this.accessoryPanelsToSync = new Set();
    this.accessoryObservedGrids = new WeakSet();

    if ('ResizeObserver' in window) {
      this.accessoryResizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const panel = entry.target.closest('[data-new-header-panel]');
          if (panel) this.scheduleAccessoryCardSync(panel);
        });
      });
    }

    this.accessoryMutationObserver = new MutationObserver(() => {
      this.panels.forEach((panel) => this.prepareAccessoryCardSync(panel));
    });
    this.accessoryMutationObserver.observe(this.querySelector('[data-new-header-panels]') || this, {
      childList: true,
      subtree: true,
    });

    const options = { signal: this.abortController.signal };

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('pointerenter', () => this.open(trigger), options);
      trigger.addEventListener('pointerleave', () => this.scheduleClose(), options);
      trigger.addEventListener('focusin', () => this.open(trigger), options);
      trigger.addEventListener('click', () => this.open(trigger), options);
      trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event, trigger), options);
    });

    this.links.forEach((link) => {
      link.addEventListener('pointerenter', () => this.close(), options);
      link.addEventListener('focusin', () => this.close(), options);
      link.addEventListener('keydown', (event) => this.onMenuKeydown(event, link), options);
    });

    this.panels.forEach((panel) => {
      panel.addEventListener('pointerenter', () => this.cancelClose(), options);
      panel.addEventListener('pointerleave', () => this.scheduleClose(), options);
      panel.addEventListener('focusin', () => this.cancelClose(), options);
      panel.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.scheduleClose();
      }, options);
    });

    this.overlay?.addEventListener('click', () => this.close(), options);
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.close();
    }, options);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.activePanel) this.close({ restoreFocus: true });
    }, options);
    document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), options);
    this.desktopMedia.addEventListener('change', (event) => {
      if (!event.matches) this.close();
    }, options);

    this.panels.forEach((panel) => this.prepareAccessoryCardSync(panel));
  }

  disconnectedCallback() {
    this.cancelClose();
    if (this.accessorySyncFrame) cancelAnimationFrame(this.accessorySyncFrame);
    this.accessoryResizeObserver?.disconnect();
    this.accessoryMutationObserver?.disconnect();
    this.abortController?.abort();
    this.initialized = false;
  }

  getPanel(trigger) {
    const panelId = trigger.getAttribute('aria-controls');
    return panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
  }

  open(trigger) {
    if (!this.desktopMedia.matches) return;

    const panel = this.getPanel(trigger);
    if (!panel) return;

    this.cancelClose();
    if (this.activeTrigger === trigger && this.activePanel === panel) return;

    this.setClosedState(this.activeTrigger, this.activePanel);
    this.activeTrigger = trigger;
    this.activePanel = panel;
    trigger.setAttribute('aria-expanded', 'true');
    trigger.classList.add('is-active');
    panel.setAttribute('aria-hidden', 'false');
    panel.setAttribute('open', '');
    panel.classList.add('is-active');
    this.classList.add('is-menu-open');
    this.prepareAccessoryCardSync(panel);
  }

  prepareAccessoryCardSync(panel) {
    const productGrid = panel.querySelector('.new-header-accessories-menu__products .product-grid');
    const featureCard = panel.querySelector('.new-header-accessories-menu__feature > .product-card');
    if (!productGrid || !featureCard) return;

    if (this.accessoryResizeObserver && !this.accessoryObservedGrids.has(productGrid)) {
      this.accessoryObservedGrids.add(productGrid);
      this.accessoryResizeObserver.observe(productGrid);
    }

    productGrid.querySelectorAll('img').forEach((image) => {
      if (image.complete || image.dataset.accessoryHeightListener === 'true') return;

      image.dataset.accessoryHeightListener = 'true';
      image.addEventListener('load', () => this.scheduleAccessoryCardSync(panel), {
        once: true,
        signal: this.abortController.signal,
      });
    });

    this.scheduleAccessoryCardSync(panel);
  }

  scheduleAccessoryCardSync(panel) {
    this.accessoryPanelsToSync.add(panel);
    if (this.accessorySyncFrame) return;

    this.accessorySyncFrame = requestAnimationFrame(() => {
      this.accessoryPanelsToSync.forEach((item) => this.syncAccessoryCardHeight(item));
      this.accessoryPanelsToSync.clear();
      this.accessorySyncFrame = null;
    });
  }

  syncAccessoryCardHeight(panel) {
    if (!this.desktopMedia.matches || !panel.isConnected) return;

    const productCards = [...panel.querySelectorAll('.new-header-accessories-menu__products .product-card')];
    const featureCard = panel.querySelector('.new-header-accessories-menu__feature > .product-card');
    const cardHeights = productCards
      .map((card) => card.getBoundingClientRect().height)
      .filter((height) => height > 0);

    if (!featureCard || cardHeights.length === 0) return;

    featureCard.style.height = `${Math.ceil(Math.max(...cardHeights))}px`;
  }

  toggle(trigger) {
    if (this.activeTrigger === trigger) {
      this.close();
      return;
    }

    this.open(trigger);
  }

  close({ restoreFocus = false } = {}) {
    this.cancelClose();
    const trigger = this.activeTrigger;

    this.setClosedState(this.activeTrigger, this.activePanel);
    this.activeTrigger = null;
    this.activePanel = null;
    this.classList.remove('is-menu-open');

    if (restoreFocus) trigger?.focus();
  }

  setClosedState(trigger, panel) {
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.classList.remove('is-active');
    panel?.setAttribute('aria-hidden', 'true');
    panel?.removeAttribute('open');
    panel?.classList.remove('is-active');
  }

  scheduleClose() {
    this.cancelClose();
    this.closeTimer = window.setTimeout(() => this.close(), this.closeDelay);
  }

  cancelClose() {
    if (!this.closeTimer) return;

    window.clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  onTriggerKeydown(event, trigger) {
    if (event.key === 'Enter' && trigger.matches('a[href]')) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle(trigger);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.open(trigger);
      requestAnimationFrame(() => {
        this.activePanel?.querySelector('a[href], button:not([disabled]), [tabindex="0"]')?.focus();
      });
      return;
    }

    this.onMenuKeydown(event, trigger);
  }

  onMenuKeydown(event, control) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    const currentIndex = this.menuControls.indexOf(control);
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (currentIndex + direction + this.menuControls.length) % this.menuControls.length;
    this.menuControls[nextIndex]?.focus();
  }

  onBlockSelect(event) {
    const panel = this.panels.find((item) => item.dataset.blockId === event.detail?.blockId);
    if (!panel) return;

    const trigger = this.triggers.find((item) => item.getAttribute('aria-controls') === panel.id);
    if (trigger) this.open(trigger);
  }
};

if (!customElements.get('new-header-navigation')) {
  customElements.define('new-header-navigation', NewHeaderNavigation);
}
